import React, { useState } from "react";
import {
  Alert,
  Grid,
  Typography,
  IconButton,
} from "@mui/material";
import { Button } from "../common/Button";
import { IAlert } from "../../interfaces/alert";
import { IValidation } from "../../interfaces/form";
import {
  IClientFormData,
  IClientFormErrors,
  IClientFormFieldsProps,
} from "../../interfaces/internalClient";
import {
  ERROR,
  ERRORS_MESSAGES,
  ECOMMERCE,
  MARKETPLACE,
  COUNTRIES,
  IVA_SITUATIONS,
} from "../../utils/constants";
import { validateInputs } from "../../utils/inputValidations";
import GenericAlert from "../GenericAlert";
import ModalBase from "./ModalBase";
import GenericForm from "../GenericForm";
import FormTextField from "../forms/FormTextField";
import FormSelectField from "../forms/FormSelectField";
import { registerClient } from "../../pages/integrations/shared/services/internalClientsService";

const {
  emailInvalid,
  emailRequired,
  passwordRequired,
  passwordInvalid,
  securityCodeRequired,
  entityRequired,
  countryRequired,
  cuitRequired,
  userRequired,
  nameRequired,
  contractOwnerRequired
} = ERRORS_MESSAGES;

interface IAddClientModalProps {
  open: boolean;
  setOpen: (_open: boolean) => void;
}


const BASE_VALIDATIONS: IValidation[] = [
  { id: "email", type: "required", message: emailRequired },
  { id: "email", type: "email", message: emailInvalid },
  { id: "password", type: "required", message: passwordRequired },
  { id: "password", type: "password", message: passwordInvalid },
  { id: "userName", type: "required", message: userRequired },
  { id: "entityName", type: "required", message: nameRequired },
  { id: "entity", type: "required", message: entityRequired },
  { id: "country", type: "required", message: countryRequired },
  { id: "cuit", type: "required", message: cuitRequired },
  { id: "securityCode", type: "required", message: securityCodeRequired },
];

const ENTITY_OPTIONS = [
  { value: ECOMMERCE, label: "Ecommerce" },
  { value: MARKETPLACE, label: "Marketplace" },
];

const COUNTRY_OPTIONS = Object.entries(COUNTRIES).map(([code, name]) => ({
  value: code,
  label: name,
}));

const CONTRACT_OWNER_OPTIONS = [
  { value: "marketplace", label: "Marketplace (gestiona contratos propios)" },
  { value: "merchant", label: "Merchant (cada merchant carga sus contratos)" },
];

const IVA_SITUATION_OPTIONS = IVA_SITUATIONS.map((v) => ({ value: v, label: v }));

const INITIAL_FORM_DATA: IClientFormData = {
  email: "",
  password: "",
  userName: "",
  entityName: "",
  entity: "",
  country: "",
  contractOwner: "",
  cuit: "",
  businessName: "",
  ivaSituation: "",
  commercialAddress: "",
  webSite: "",
  billingEmail: "",
  securityCode: "",
};

const INITIAL_ERRORS: IClientFormErrors = {
  email: [],
  password: [],
  userName: [],
  entityName: [],
  entity: [],
  country: [],
  contractOwner: [],
  cuit: [],
  securityCode: [],
};

const ClientFormFields: React.FC<IClientFormFieldsProps> = ({
  formData,
  errors,
  showPassword,
  isMarketplace,
  onFieldChange,
  passwordToggle,
}) => (
  <>
    {/* ── Datos de cuenta ── */}
    <Typography variant="subtitle2" color="textSecondary" sx={{ mt: 1 }}>
      Datos de cuenta
    </Typography>
    <FormTextField
      label="Correo electrónico"
      name="email"
      value={formData.email}
      placeholder="Ingresar correo electrónico"
      errors={errors.email}
      onChange={onFieldChange("email")}
      autoFocus
      className=""
    />
    <FormTextField
      label="Contraseña"
      name="password"
      type={showPassword ? "text" : "password"}
      value={formData.password}
      placeholder="Ingresar contraseña"
      errors={errors.password}
      onChange={onFieldChange("password")}
      endAdornment={passwordToggle}
    />
    <FormTextField
      label="Nombre completo del responsable"
      name="userName"
      value={formData.userName}
      placeholder="Ej: Juan Pérez"
      errors={errors.userName}
      onChange={onFieldChange("userName")}
    />
    {/* ── Datos de entidad ── */}
    <Typography variant="subtitle2" color="textSecondary" sx={{ mt: 2 }}>
      Datos de entidad
    </Typography>
    <FormTextField
      label="Alias de entidad"
      name="entityName"
      value={formData.entityName}
      placeholder="Ej: mitienda (identificador único)"
      errors={errors.entityName}
      onChange={onFieldChange("entityName")}
    />
    <FormSelectField
      label="Tipo de negocio"
      name="entity"
      value={formData.entity}
      placeholder="Seleccionar tipo de negocio"
      options={ENTITY_OPTIONS}
      errors={errors.entity}
      onChange={onFieldChange("entity")}
    />
    <FormSelectField
      label="País"
      name="country"
      value={formData.country}
      placeholder="Seleccionar país"
      options={COUNTRY_OPTIONS}
      errors={errors.country}
      onChange={onFieldChange("country")}
    />
    {/* contractOwner solo para marketplace */}
    {isMarketplace && (
      <>
        <FormSelectField
          label="Propietario de contratos de operadores"
          name="contractOwner"
          value={formData.contractOwner}
          placeholder="Seleccionar propietario de contratos"
          options={CONTRACT_OWNER_OPTIONS}
          errors={errors.contractOwner}
          onChange={onFieldChange("contractOwner")}
        />
        <Alert severity="warning" sx={{ mb: 1, fontSize: "0.78rem" }}>
          Este valor es permanente. No podrá modificarse una vez
          configurados los operadores logísticos.
        </Alert>
      </>
    )}
    {/* ── Datos fiscales ── */}
    <Typography variant="subtitle2" color="textSecondary" sx={{ mt: 2 }}>
      Datos fiscales
    </Typography>
    <FormTextField
      label="CUIT"
      name="cuit"
      value={formData.cuit}
      placeholder="Ej: 30123456789"
      errors={errors.cuit}
      onChange={onFieldChange("cuit")}
    />
    <FormTextField
      label="Razón social (opcional)"
      name="businessName"
      value={formData.businessName}
      placeholder="Ej: Mi Empresa S.A."
      errors={[]}
      onChange={onFieldChange("businessName")}
    />
    <FormSelectField
      label="Condición ante el IVA (opcional)"
      name="ivaSituation"
      value={formData.ivaSituation}
      placeholder="Seleccionar condición"
      options={IVA_SITUATION_OPTIONS}
      errors={[]}
      onChange={onFieldChange("ivaSituation")}
    />
    {/* ── Datos de contacto ── */}
    <Typography variant="subtitle2" color="textSecondary" sx={{ mt: 2 }}>
      Datos de contacto (opcionales)
    </Typography>
    <FormTextField
      label="Domicilio comercial"
      name="commercialAddress"
      value={formData.commercialAddress}
      placeholder="Ej: Av. Corrientes 1234, CABA"
      errors={[]}
      onChange={onFieldChange("commercialAddress")}
    />
    <FormTextField
      label="Sitio web"
      name="webSite"
      value={formData.webSite}
      placeholder="Ej: https://mitienda.com"
      errors={[]}
      onChange={onFieldChange("webSite")}
    />
    <FormTextField
      label="Email de facturación"
      name="billingEmail"
      value={formData.billingEmail}
      placeholder="Ej: facturacion@mitienda.com"
      errors={[]}
      onChange={onFieldChange("billingEmail")}
    />
    {/* ── Seguridad ── */}
    <Typography variant="subtitle2" color="textSecondary" sx={{ mt: 2 }}>
      Seguridad
    </Typography>
    <FormTextField
      label="Código de seguridad"
      name="securityCode"
      value={formData.securityCode}
      placeholder="Ingresar código de seguridad"
      errors={errors.securityCode}
      onChange={onFieldChange("securityCode")}
    />
  </>
);

const AddClientModal: React.FC<IAddClientModalProps> = ({
  open,
  setOpen,
}) => {
  const [formData, setFormData] = useState<IClientFormData>(INITIAL_FORM_DATA);
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<IClientFormErrors>(INITIAL_ERRORS);
  const [alertModal, setAlertModal] = useState({} as IAlert);
  const [disableButton, setDisableButton] = useState(false);
  const [loading, setLoading] = useState(false);

  const isMarketplace = formData.entity === MARKETPLACE;

  const handleClose = () => {
    setOpen(false);
    setErrors(INITIAL_ERRORS);
    setFormData(INITIAL_FORM_DATA);
    setDisableButton(false);
    setLoading(false);
    setAlertModal({} as IAlert);
  };

  const handleSubmit = async () => {
    const inputErrors: IClientFormErrors = { ...INITIAL_ERRORS, ...validateInputs(formData, BASE_VALIDATIONS) };

    // contractOwner is required only for marketplace
    if (isMarketplace && !formData.contractOwner) {
      inputErrors.contractOwner = [contractOwnerRequired];
    }

    setErrors(inputErrors);

    const hasErrors = Object.values(inputErrors).some((e) => e.length > 0);
    if (hasErrors || !formData.entity) return;

    setDisableButton(true);
    setLoading(true);
    try {
      await registerClient({
        email: formData.email.trim(),
        password: formData.password,
        userName: formData.userName.trim(),
        entityName: formData.entityName.trim(),
        entity: formData.entity as "marketplace" | "ecommerce",
        country: formData.country,
        contractOwner: isMarketplace
          ? (formData.contractOwner as "marketplace" | "merchant")
          : undefined,
        cuit: formData.cuit.trim(),
        businessName: formData.businessName?.trim() || undefined,
        ivaSituation: formData.ivaSituation || undefined,
        commercialAddress: formData.commercialAddress?.trim() || undefined,
        webSite: formData.webSite?.trim() || undefined,
        billingEmail: formData.billingEmail?.trim() || undefined,
        securityCode: formData.securityCode.trim(),
      });
      handleClose();
      window.location.reload();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      setAlertModal({ typeOfAlert: ERROR, message, showAlert: true });
      setDisableButton(false);
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange =
    (field: keyof IClientFormData) => (value: string) => {
      setAlertModal({ showAlert: false } as IAlert);
      setDisableButton(false);
      setFormData((prev) => {
        const updated = { ...prev, [field]: value };
        // Clear contractOwner when switching away from marketplace
        if (field === "entity" && value !== MARKETPLACE) {
          updated.contractOwner = "";
        }
        return updated;
      });
    };

  const passwordToggle = (
    <IconButton onClick={() => setShowPassword(!showPassword)}>
      <span className="material-symbols-rounded">
        {showPassword ? "visibility_off" : "visibility"}
      </span>
    </IconButton>
  );

  return (
    <ModalBase open={open} handleClose={handleClose}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography className="m-t-10 f-s-20">Añadir Cliente</Typography>
          {alertModal.showAlert && <GenericAlert alert={alertModal} />}
        </Grid>

        <Grid item xs={12} className="m-t-20">
          <GenericForm onSubmit={handleSubmit} disableSubmit={disableButton}>
            <ClientFormFields
              formData={formData}
              errors={errors}
              showPassword={showPassword}
              isMarketplace={isMarketplace}
              onFieldChange={handleFieldChange}
              passwordToggle={passwordToggle}
            />
          </GenericForm>
        </Grid>

        <Grid item xs={6} className="m-t-40">
          <Button fullWidth variant="outlined" onClick={handleClose}>
            Cancelar
          </Button>
        </Grid>
        <Grid item xs={6} className="m-t-40">
          <Button
            fullWidth
            variant="contained"
            disabled={disableButton}
            loading={loading}
            onClick={handleSubmit}
          >
            Registrar cliente
          </Button>
        </Grid>
      </Grid>
    </ModalBase>
  );
};

export default AddClientModal;
